import _ from 'lodash-es';
import angular from 'angular';

import { RoleTypes } from '@/portainer/rbac/models/role';
import { isLimitedToBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { getSettings } from '@/react/portainer/settings/settings.service';
import { extractAccessPoliciesFromPolicy } from '@/portainer/helpers/policyHelper';

class PorAccessManagementController {
  /* @ngInject */
  constructor($scope, $state, Notifications, AccessService, RoleService) {
    Object.assign(this, { $scope, $state, Notifications, AccessService, RoleService });

    this.limitedToBE = false;
    this.$state = $state;

    this.unauthorizeAccess = this.unauthorizeAccess.bind(this);
    this.updateAction = this.updateAction.bind(this);
    this.onChangeUsersAndTeams = this.onChangeUsersAndTeams.bind(this);
  }

  onChangeUsersAndTeams(value) {
    this.$scope.$evalAsync(() => {
      this.formValues.multiselectOutput = value;
    });
  }

  updateAction(updatedUserAccesses, updatedTeamAccesses) {
    const entity = this.accessControlledEntity;
    const oldUserAccessPolicies = entity.UserAccessPolicies;
    const oldTeamAccessPolicies = entity.TeamAccessPolicies;

    const accessPolicies = this.AccessService.generateAccessPolicies(oldUserAccessPolicies, oldTeamAccessPolicies, updatedUserAccesses, updatedTeamAccesses);
    this.accessControlledEntity.UserAccessPolicies = accessPolicies.userAccessPolicies;
    this.accessControlledEntity.TeamAccessPolicies = accessPolicies.teamAccessPolicies;
    this.updateAccess();
  }

  authorizeAccess() {
    const entity = this.accessControlledEntity;
    const oldUserAccessPolicies = entity.UserAccessPolicies;
    const oldTeamAccessPolicies = entity.TeamAccessPolicies;
    const selectedRoleId = this.formValues.selectedRole.Id;
    const selectedUserAccesses = _.filter(this.formValues.multiselectOutput, (access) => access.Type === 'user');
    const selectedTeamAccesses = _.filter(this.formValues.multiselectOutput, (access) => access.Type === 'team');

    const accessPolicies = this.AccessService.generateAccessPolicies(oldUserAccessPolicies, oldTeamAccessPolicies, selectedUserAccesses, selectedTeamAccesses, selectedRoleId);
    this.accessControlledEntity.UserAccessPolicies = accessPolicies.userAccessPolicies;
    this.accessControlledEntity.TeamAccessPolicies = accessPolicies.teamAccessPolicies;
    this.updateAccess();
  }

  unauthorizeAccess(selectedAccesses) {
    const entity = this.accessControlledEntity;
    const userAccessPolicies = entity.UserAccessPolicies;
    const teamAccessPolicies = entity.TeamAccessPolicies;
    const selectedUserAccesses = _.filter(selectedAccesses, (access) => access.Type === 'user');
    const selectedTeamAccesses = _.filter(selectedAccesses, (access) => access.Type === 'team');

    _.forEach(selectedUserAccesses, (access) => delete userAccessPolicies[access.Id]);
    _.forEach(selectedTeamAccesses, (access) => delete teamAccessPolicies[access.Id]);
    this.updateAccess();
  }

  isRoleLimitedToBE(role) {
    if (!this.limitedToBE) {
      return false;
    }

    return role.ID !== RoleTypes.STANDARD;
  }

  roleLabel(role) {
    if (!this.limitedToBE) {
      return role.Name;
    }

    if (this.isRoleLimitedToBE(role)) {
      return `${role.Name} (Business Edition Feature)`;
    }

    return `${role.Name} (Default)`;
  }

  async $onInit() {
    this.policy = this.policy || {};
    this.hasPolicyApplied = !!this.policy.policyID;
    this.policyName = this.policy.policyName || '';
    this.policyID = this.policy.policyID || 0;

    try {
      if (this.limitedFeature) {
        this.limitedToBE = isLimitedToBE(this.limitedFeature);
      }

      const entity = this.accessControlledEntity;
      const parent = this.inheritFrom;

      // If a policy is applied to a group, extract access policies from the policy data
      // For groups, the backend doesn't populate UserAccessPolicies/TeamAccessPolicies from the policy
      // like it does for endpoints, so we need to extract them from the policy's Data
      if (this.hasPolicyApplied && this.entityType === 'group' && this.policy.policyData) {
        const extractedPolicies = extractAccessPoliciesFromPolicy(this.policy.policyData);
        // Temporarily populate the entity with policy-based access policies for display
        entity.UserAccessPolicies = extractedPolicies.userAccessPolicies;
        entity.TeamAccessPolicies = extractedPolicies.teamAccessPolicies;
      }

      const roles = await this.RoleService.roles(this.entityType, entity);
      this.roles = _.orderBy(roles, 'Priority', 'asc');
      this.formValues = {
        multiselectOutput: [],
        selectedRole: this.roles.find((role) => !this.isRoleLimitedToBE(role)),
      };

      // as this is rendered only for admins, we can safely assume that settings query will succeed
      // also as we don't render elements relying on 'edgeComputeEnabled' in the registries accesses view
      // skip the request for this situation
      if (this.entityType !== 'registry') {
        const { EnableEdgeComputeFeatures } = await getSettings();
        this.edgeComputeEnabled = EnableEdgeComputeFeatures;
      }

      const data = await this.AccessService.accesses(entity, parent, this.roles);

      if (this.filterUsers) {
        data.availableUsersAndTeams = this.filterUsers(data.availableUsersAndTeams);
      }

      this.availableUsersAndTeams = _.orderBy(data.availableUsersAndTeams, 'Name', 'asc');
      this.authorizedUsersAndTeams = data.authorizedUsersAndTeams;
    } catch (err) {
      this.$state.go('portainer.home');
      this.availableUsersAndTeams = [];
      this.authorizedUsersAndTeams = [];
      this.Notifications.error('Failure', err, 'Unable to retrieve accesses');
    }
  }
}

export default PorAccessManagementController;
angular.module('portainer.app').controller('porAccessManagementController', PorAccessManagementController);
